import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// StapKompas Verkeer — organizer side of the SAME traffic_controller_chat_messages
// rows/channels field controllers already use from the separate verkeer-app/
// (see tc_chat_send_message/tc_chat_list_messages in backend/server.py). Not a
// second chat system: GET/POST /admin/events/{event_id}/traffic-chat/messages
// read/write the identical table, so a message sent here shows up for a
// controller within one poll cycle, and vice versa. Polled, not Realtime — same
// reasoning as the rest of this admin app's traffic-controller-adjacent pages
// (a controller has no Supabase-recognized identity to subscribe against).

const POLL_MS = 5000;

export default function AdminTrafficChat() {
  const { authAxios } = useAuth();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [routes, setRoutes] = useState([]);
  const [channelRouteId, setChannelRouteId] = useState(""); // "" = event-wide

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const sinceRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/events");
        if (cancelled) return;
        const items = (data.items || []).filter((e) => e.traffic_control_enabled);
        setEvents(items);
        setEventId((current) => (items.some((e) => e.id === current) ? current : items[0]?.id || ""));
      } catch (error) {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authAxios]);

  // Flat route list across every day of the event — just used to label the
  // route-channel picker, same data AdminTrafficControllers.jsx already fetches.
  useEffect(() => {
    setRoutes([]);
    setChannelRouteId("");
    if (!eventId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data: daysRes } = await authAxios.get(`/admin/events/${eventId}/days`);
        const days = daysRes.items || [];
        const routeLists = await Promise.all(
          days.map((d) => authAxios.get(`/admin/event-days/${d.id}/routes`).then((r) => r.data.items || []))
        );
        if (!cancelled) setRoutes(routeLists.flat());
      } catch (error) {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authAxios, eventId]);

  const loadMessages = async (reset) => {
    if (!eventId) return;
    try {
      const params = { route_id: channelRouteId || undefined };
      if (!reset && sinceRef.current) params.since = sinceRef.current;
      const { data } = await authAxios.get(`/admin/events/${eventId}/traffic-chat/messages`, { params });
      const items = data.items || [];
      if (items.length) {
        sinceRef.current = items[items.length - 1].created_at;
        setMessages((prev) => (reset ? items : [...prev, ...items]));
      } else if (reset) {
        setMessages([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    }
  };

  useEffect(() => {
    sinceRef.current = null;
    setMessages([]);
    if (!eventId) return undefined;
    loadMessages(true);
    const timer = setInterval(() => loadMessages(false), POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios, eventId, channelRouteId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !eventId) return;
    setSending(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/traffic-chat/messages`, {
        message: text,
        route_id: channelRouteId || null,
      });
      setDraft("");
      await loadMessages(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-traffic-chat-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 flex items-center justify-center">
          <MessageSquare className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Chat</h1>
          <p className="text-sm text-slate-400">
            StapKompas Verkeer — hetzelfde kanaal als de verkeersregelaars/sluiter in hun eigen app. Een bericht
            dat je hier stuurt komt binnen één poll-cyclus bij hen aan, en omgekeerd.
          </p>
        </div>
      </div>

      {loadingEvents && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Events laden…
        </div>
      )}

      {!loadingEvents && events.length === 0 && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400">
          Er is nog geen event waarvoor StapKompas Verkeer actief is.
        </div>
      )}

      {!loadingEvents && events.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            {events.length > 1 && (
              <label className="block max-w-xs flex-1 min-w-[160px]">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Event</span>
                <select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  data-testid="traffic-chat-event-select"
                  className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                >
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>{event.title}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="block max-w-xs flex-1 min-w-[160px]">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Kanaal</span>
              <select
                value={channelRouteId}
                onChange={(e) => setChannelRouteId(e.target.value)}
                data-testid="traffic-chat-channel-select"
                className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
              >
                <option value="">Event-breed</option>
                {routes.map((route) => (
                  <option key={route.id} value={route.id}>{route.title}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="rounded-2xl border border-svj-border bg-svj-card overflow-hidden flex flex-col h-[480px]">
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="traffic-chat-messages">
              {messages.length === 0 ? (
                <p className="text-sm text-slate-500">Nog geen berichten in dit kanaal.</p>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`traffic-chat-message-${m.id}`}
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      m.sender_type === "organizer"
                        ? "bg-svj-orange/15 border border-svj-orange/30 ml-auto text-right"
                        : "bg-svj-bg border border-svj-border"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">
                      {m.sender_display_name}
                    </div>
                    <div className="text-white whitespace-pre-line">{m.message}</div>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-svj-border">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Typ een bericht…"
                data-testid="traffic-chat-input"
                className="flex-1 h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                data-testid="traffic-chat-send"
                className="h-10 px-3 rounded-lg bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Versturen
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
